/**
 * React Query hooks for the Signing Approvals screen.
 *
 * Query-key family mirrors the legacy dashboard hook
 * (useSigningQuery.js): everything is nested under ['signing', …], and
 * every mutation invalidates the whole ['signing'] family on success so
 * the stats KPIs, the pending queue, and the audit log all re-fetch
 * after an approve / reject / revoke / tier change.
 *
 * Polling cadences match the legacy hook + view:
 *   - stats:          refetchInterval 30s
 *   - pending queue:  refetchInterval 15s (set by the view, passed through)
 *   - audit log:      no poll (append-only; stale entries can only be behind)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveSigningRequest,
  AuditLogListParams,
  fetchAuditLog,
  fetchAuditLogEntry,
  fetchSigningRequest,
  fetchSigningRequests,
  fetchSigningStats,
  rejectSigningRequest,
  revokeSignedBinary,
  setCreatorTier,
  SigningRequestListParams
} from '@/api/signing';

export function useSigningStats() {
  return useQuery({
    queryKey: ['signing', 'stats'],
    queryFn: fetchSigningStats,
    refetchInterval: 30_000
  });
}

export function useSigningRequests(
  params: SigningRequestListParams,
  options?: { refetchInterval?: number }
) {
  return useQuery({
    queryKey: ['signing', 'requests', params],
    queryFn: () => fetchSigningRequests(params),
    placeholderData: prev => prev,
    refetchInterval: options?.refetchInterval
  });
}

export function useSigningRequest(id: string | null) {
  return useQuery({
    queryKey: ['signing', 'request', id],
    queryFn: () => fetchSigningRequest(id as string),
    enabled: !!id
  });
}

export function useAuditLog(params: AuditLogListParams) {
  return useQuery({
    queryKey: ['signing', 'audit-log', params],
    queryFn: () => fetchAuditLog(params),
    placeholderData: prev => prev
  });
}

export function useAuditLogEntry(id: string | null) {
  return useQuery({
    queryKey: ['signing', 'audit-log', id],
    queryFn: () => fetchAuditLogEntry(id as string),
    enabled: !!id
  });
}

export function useApproveSigningRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveSigningRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signing'] });
    }
  });
}

export function useRejectSigningRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      rejectSigningRequest(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signing'] });
    }
  });
}

export function useRevokeSignedBinary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      revokeSignedBinary(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signing'] });
    }
  });
}

export function useSetCreatorTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { creatorId: string; tier: import('@/api/signing').SigningTier }) =>
      setCreatorTier(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signing'] });
    }
  });
}
